<template>
  <PortalLayout section="Roles">
    <div class="mt-4">
      <div v-if="isLoading"><Loading /></div>
      <div v-else>
        <div v-if="roles && roles.length > 0">
          <span class="w-full flex my-2" v-if="roleOrderChanged">
            <p class="grow text-red-700">
              Your sort order changes are unsaved, when done sorting, please hit the 'Save
              Changes' button to save
            </p>
            <SaveButton @click="saveSortedRoles" />
          </span>
          <p class="grow text-red-900 mb-2">
            * The topmost role will be used as the default role for single sign on
            registration
          </p>
          <AddButton @click="addClick(true)">Add Role</AddButton>
          <div
            v-for="role in roles"
            :key="role.siteRoleId"
            class="bg-gray-100 border-t flex my-2"
          >
            <div class="flex-col mx-2">
              <i-heroicons-outline-chevron-up
                class="h-5 w-5 mt-1 mb-2"
                :class="
                  role.sortOrder == Math.min(...roles.map((r) => r.sortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapRoleSort(role.sortOrder, true)"
              />
              <i-heroicons-outline-chevron-down
                class="h-5 w-5"
                :class="
                  role.sortOrder == Math.max(...roles.map((r) => r.sortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapRoleSort(role.sortOrder, false)"
              />
            </div>
            <div
              class="flex-col pl-2 pr-4 py-3 cursor-pointer hover:bg-blue-200 grow h-full"
              @click="editClick(role)"
            >
              <div class="text-lg">{{ role.roleAlias }}</div>
              <div v-if="role.isStaffRole" class="ml-2 font-medium text-gray-700">
                <span> Staff </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mb-2">
          <span> -- No Roles found for this building -- </span>
        </div>
        <div>
          <AddButton @click="addClick(false)">Add Role</AddButton>
        </div>
        <EditPopup
          entity-name="Role"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="saveError"
          :delete-enabled="true"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseSelect
            v-model="roleTypeId"
            label="Role Type"
            :options="roleTypeValues"
            :error="errors.roleTypeId"
          />
          <BaseInput
            type="text"
            label="Role Alias"
            v-model="roleAlias"
            :error="errors.roleAlias"
          />
          <!-- <div class="col-span-12">
            <BaseCheckbox label="Enable Staff Roles" v-model="staffRolesEnabled" />
            </div> -->
        </EditPopup>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useAdminStore } from "../../store/adminStore";
import { SiteRoleDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();
const adminStore = useAdminStore();

let isLoading = ref(false);
var property = computed(() => bldgStore.property);

var roles = ref(null as SiteRoleDTO[]);
//var roleTypes = computed(() => adminStore.roleTypes);

var roleTypeValues = computed(() => {
  let types = adminStore.roleTypes;
  if (types && types.length > 0) {
    return types.map((t) => {
      return {
        name: t.name + (t.isStaffRole ? " (Staff)" : ""),
        value: t.roleTypeId,
      } as NameValue;
    }) as NameValue[];
  }
  return [] as NameValue[];
});

onMounted(loadData);
watch(property, loadData);

async function loadData() {
  isLoading.value = true;
  if (property?.value?.portalId) {
    await bldgStore.getInviteCodes(property.value.portalId);
  }
  await adminStore.getRoleTypes();

  //Assign fresh sortOrder values based off the current order (in case of unmigrated)
  let newSortValue = 0;
  roles.value = bldgStore.property?.siteRoles.map((role) => {
    return {
      ...role,
      sortOrder: newSortValue++,
    };
  });

  isLoading.value = false;
}

// Popup stuff
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    roleTypeId: yup
      .number()
      .typeError("Please select a role type.")
      .required()
      .label("Role Type"),
    roleAlias: yup.string().required().max(50).label("Role Alias"),
  }),
  initialValues: {
    roleTypeId: 0,
    roleAlias: "",
  },
});

let siteRoleId = ref(0);
const { value: roleTypeId } = useField<number>("roleTypeId");
const { value: roleAlias } = useField<string>("roleAlias");
let roleSortOrder = ref(-1);

watch(roleTypeId, () => {
  if (roleAlias.value.length === 0)
    roleAlias.value = roleTypeValues.value.find((r) => r.value == roleTypeId.value).name;
});

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveRole = {
    siteRoleId: siteRoleId.value,
    roleTypeId:
      typeof roleTypeId.value === "string"
        ? Number.parseInt(roleTypeId.value)
        : roleTypeId.value,
    roleAlias: roleAlias.value,
    sortOrder: roleSortOrder.value,
  } as SiteRoleDTO;

  console.log("Saving portal role", saveRole);
  saveError.value = "";

  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.saveSiteRole(property.value?.portalId, saveRole);
    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      saveError.value = possibleError;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let addClick = function (top: boolean) {
  siteRoleId.value = 0;
  roleTypeId.value = 0;
  roleAlias.value = "";

  if (top) {
    roleSortOrder.value = Math.min(...roles.value.map((d) => d.sortOrder)) - 1;
  } else {
    roleSortOrder.value = Math.max(...roles.value.map((d) => d.sortOrder)) + 1;
  }

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedRole: SiteRoleDTO) {
  siteRoleId.value = selectedRole.siteRoleId ?? 0;
  roleTypeId.value = selectedRole.roleTypeId ?? 0;
  roleAlias.value = selectedRole.roleAlias ?? "";
  roleSortOrder.value = selectedRole.sortOrder ?? -1;

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && siteRoleId.value > 0) {
    saveError.value = await bldgStore.deleteSiteRole(siteRoleId.value);
    if (!saveError.value) {
      isPopupVisible.value = false;
      await bldgStore.tryGetBuildingData(property.value.siteCode, true);
    }
  }
}

// Sorting
var roleOrderChanged = ref(false);

function swapRoleSort(sortID: number, sortUp: boolean) {
  let selectedDocID = roles.value.find((role) => role.sortOrder == sortID).siteRoleId;

  if (sortUp) {
    let swapDocID = roles.value.find((role) => role.sortOrder == sortID - 1).siteRoleId;
    roles.value.find((role) => role.siteRoleId == selectedDocID).sortOrder = sortID - 1;
    roles.value.find((role) => role.siteRoleId == swapDocID).sortOrder = sortID;

    roleOrderChanged.value = true;
  } else {
    let swapDocID = roles.value.find((role) => role.sortOrder == sortID + 1).siteRoleId;
    roles.value.find((role) => role.siteRoleId == selectedDocID).sortOrder = sortID + 1;
    roles.value.find((role) => role.siteRoleId == swapDocID).sortOrder = sortID;

    roleOrderChanged.value = true;
  }

  sortAllDocs();
}

function sortAllDocs() {
  roles.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

async function saveSortedRoles() {
  let orderToSave = {
    siteRoleOrders: roles.value.map((role) => {
      return {
        siteRoleId: role.siteRoleId,
        sortOrder: role.sortOrder,
      };
    }),
  };

  saveError.value = await bldgStore.saveRoleOrder(orderToSave);
  if (saveError.value == null) roleOrderChanged.value = false;
  loadData();
}
</script>
